import { useEffect, useLayoutEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { getAsset } from "../lib/generate/getAsset.js";
import { layoutsForTemplate } from "../lib/display/templates.js";
import { renderBannerSrcdoc, resolveSlots, isAdvanced, hasLayerContent, advancedTimelineDuration, presetDuration } from "../lib/display/runtime.js";
import { isSizeLocked, buildLockedSnapshot, bakeLockedSnapshot } from "../lib/display/lockedSnapshots.js";
import { buildRenderContext } from "../lib/display/context.js";
import { slotCharBudget, isRegulated, canonicalCompliance } from "../lib/display/doc.js";
import { layersFromTemplate } from "../lib/display/layers.js";
import { layerImageAt } from "../lib/display/layerHelpers.js";
import { WIZARD_TEMPLATES, LIMITS, displaySize, canEdit } from "../constants.js";
import { uploadReference } from "../lib/references/uploadReference.js";
import { approveAsset } from "../lib/export/approveAsset.js";
import { revertToDraft } from "../lib/export/revertToDraft.js";
import EditingPanel from "./ComposeFields.js";
import LayersPanel, { LayerEditor } from "./ComposeLayers.js";
import ComposeTimelinePanel from "./ComposeTimelinePanel.js";
import ComposeOverlay from "./ComposeOverlay.js";
import ExportDialog from "./ExportDialog.js";
import SitePreviewDialog from "./SitePreviewDialog.js";
import "./Compose.css";
import "./ComposeToolbar.css";

// Simple/advanced mode bypass: flip to true to restore the "back to
// simple editing" button (see the auto-conversion effect earlier in
// this file for the other half of this change).
const SHOW_EXIT_TO_SIMPLE_MODE = false;

const STATUS_LABEL = { draft: "Draft", approved: "Approved", exported: "Exported" };

/** Largest zoom (snapped to 0.25 increments) that fits a banner of
 * bannerW x bannerH within an actually-measured available space,
 * respecting both dimensions - a tall, narrow banner is bound by
 * available height, a wide one by available width.
 *
 * Uses floor, not round-to-nearest, when snapping to the 0.25 grid -
 * confirmed via direct reproduction that round-to-nearest can produce a
 * zoom whose resulting canvas size exceeds the measured available space
 * (e.g. a raw ratio of 1.90 rounds up to 2.00, producing a canvas 25px
 * taller than what's actually available, which is exactly what was
 * triggering the scrollbar). Floor always rounds toward the smaller,
 * safe increment, so the result can never exceed what was measured.
 */
function computeFitZoom(bannerW, bannerH, availableW, availableH) {
  if (!availableW || !availableH || bannerW <= 0 || bannerH <= 0) return 1;
  const raw = Math.min(availableW / bannerW, availableH / bannerH);
  return Math.max(0.25, Math.min(4, Math.floor(raw / 0.25) * 0.25));
}
const clampZoom = (z) => Math.max(0.25, Math.min(4, Math.round(z / 0.25) * 0.25));

function layerName(layer) {
  return layer.name ?? layer.role ?? layer.type;
}

/**
 * A key that changes whenever a resolved image URL a size's preview depends
 * on changes - forces the iframe to remount rather than relying on the
 * browser to notice a srcDoc-only update, which isn't always reliable.
 * Respects per-size overrides (advanced: per-layer images; slots: the
 * size's own resolved image/logo, not just the master).
 */
function previewKeyFor(sizeId, doc, references, replayKeyVal) {
  const urlFor = (source) =>
    source?.kind === "reference" ? (references ?? []).find((r) => r.id === source.id)?.url ?? "" : "";

  if (hasLayerContent(doc)) {
    const bgUrl = urlFor(doc.advancedBackground);
    const imgs = (doc.layers ?? [])
      .filter((l) => l.type === "image" && l.boxes[sizeId] && !l.boxes[sizeId].hidden)
      .map((l) => urlFor(layerImageAt(l, sizeId)))
      .join(",");
    return `${sizeId}-${replayKeyVal}-adv-${bgUrl}-${imgs}`;
  }

  const { slots } = resolveSlots(doc, sizeId);
  const logoUrl = (references ?? []).find((r) => r.id === slots.logoRefId)?.url ?? "";
  const imageUrl = urlFor(slots.imageSource);
  return `${sizeId}-${replayKeyVal}-${logoUrl}-${imageUrl}`;
}

/** How a layer's box changed between two docs (move / resize / show / hide). */
function boxChange(la, lb) {
  const sizes = new Set([...Object.keys(la.boxes), ...Object.keys(lb.boxes)]);
  for (const s of sizes) {
    const A = la.boxes[s];
    const B = lb.boxes[s];
    if (!A && B) return B.hidden ? null : "Placed";
    if (A && !B) return null;
    if (!A || !B) continue;
    if (!!A.hidden !== !!B.hidden) return B.hidden ? "Hid" : "Showed";
    if (A.w !== B.w || A.h !== B.h) return "Resized";
    if (A.x !== B.x || A.y !== B.y) return "Moved";
  }
  return null;
}

const imgKey = (l) => JSON.stringify([l.imageSource, l.imageBySize]);

/**
 * A specific label for the change that turned doc `a` into `b` — naming the
 * element and what happened, for the history panel. lockedSnapshots always
 * reads as empty here — that feature isn't built yet, so those two branches
 * simply never fire, not a bug.
 */
function describeChange(a, b) {
  const aLayers = a.layers ?? [];
  const bLayers = b.layers ?? [];
  const aById = new Map(aLayers.map((l) => [l.id, l]));
  const bById = new Map(bLayers.map((l) => [l.id, l]));

  const added = bLayers.find((l) => !aById.has(l.id));
  if (added) return `Add ${layerName(added)}`;
  const removed = aLayers.find((l) => !bById.has(l.id));
  if (removed) return `Remove ${layerName(removed)}`;

  const lockA = Object.keys(a.lockedSnapshots ?? {}).length;
  const lockB = Object.keys(b.lockedSnapshots ?? {}).length;
  if (lockB > lockA) return "Lock banner";
  if (lockB < lockA) return "Unlock banner";
  if ((a.advanced ?? false) !== (b.advanced ?? false)) return b.advanced ? "Customize layout" : "Exit to template";
  if ((a.advancedBackground?.id ?? "") !== (b.advancedBackground?.id ?? "")) return "Background image";
  if (a.slots.bgColor !== b.slots.bgColor) return "Background colour";
  if (a.deliveryProfile !== b.deliveryProfile) return "Delivery profile";
  if (a.animationPreset !== b.animationPreset) return "Animation";

  for (const lb of bLayers) {
    const la = aById.get(lb.id);
    if (!la || la === lb) continue;
    const name = layerName(lb);
    if ((la.name ?? "") !== (lb.name ?? "")) return `Rename → ${name}`;
    if ((la.text ?? "") !== (lb.text ?? "")) return `Text — ${name}`;
    if ((la.color ?? "") !== (lb.color ?? "") || (la.bg ?? "") !== (lb.bg ?? "")) return `Colour — ${name}`;
    if (la.fontSize !== lb.fontSize || la.fontWeight !== lb.fontWeight || la.align !== lb.align || la.fontSizeAuto !== lb.fontSizeAuto) return `Style — ${name}`;
    const moved = boxChange(la, lb);
    if (moved) return `${moved} ${name}`;
    if (la.z !== lb.z || JSON.stringify(la.zBySize ?? {}) !== JSON.stringify(lb.zBySize ?? {})) return `Reorder ${name}`;
    if (imgKey(la) !== imgKey(lb)) return `Image — ${name}`;
    if (la.type !== lb.type) return `${name} → ${lb.type}`;
    return `Edit ${name}`;
  }
  return "Edit";
}

export default function Compose({ config, onUpdateAsset, isSavingAsset, onSaveReferences }) {
  const { assetId } = useParams();
  const [searchParams] = useSearchParams();
  const brandSlug = searchParams.get("brand");

  const entry = (config?.config ?? []).find((item) => item.brand.slug === brandSlug);

  const [asset, setAsset] = useState(null);
  const [doc, setDoc] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  // Approve is now a direct, synchronous status flip (no build/validation
  // involved at all) - gatePending just covers that one, fast request.
  const [gatePending, setGatePending] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [siteOpen, setSiteOpen] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [activeSizeId, setActiveSizeId] = useState(null);
  // null = editing the shared/master slots; a sizeId = editing that size's
  // own override. Same distinction the original makes.
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedLayerId, setSelectedLayerId] = useState(null);
  // null = grid view; a sizeId = the dedicated single-banner editing view
  // (advanced mode only). Separate from selectedSize/activeSizeId, same as
  // the original.
  const [focusSize, setFocusSize] = useState(null);
  const [zoom, setZoom] = useState(1);

  // Undo/redo + copy/paste. No backend coupling here — update() just
  // snapshots and calls setDoc; persisting still only happens via the
  // manual Save button.
  const docRef = useRef(null);
  const historyRef = useRef({ past: [], future: [] });
  const lastSnapRef = useRef(0);
  const clipboardRef = useRef(null);
  const [histTick, setHistTick] = useState(0); // forces re-render on history changes (historyRef is a ref)
  const [historyOpen, setHistoryOpen] = useState(false);
  // Set true while the user scrubs the timeline, to suspend the preview auto-loop.
  const previewPausedRef = useRef(false);
  const [replayKey, setReplayKey] = useState(0);

  useEffect(() => {
    if (!entry) return;
    let cancelled = false;
    setError(null);

    (async () => {
      try {
        const result = await getAsset(
          config,
          { brandId: entry.brand.id, assetId },
          setIsLoading,
          setApiResponse
        );
        if (!cancelled) {
          setAsset(result);
          setDoc(result.content);
          docRef.current = result.content;
          setActiveSizeId(result.content?.sizes?.[0] ?? null);
        }
      } catch (err) {
        if (!cancelled) setError("Couldn't load this asset. Please try again.");
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, entry?.brand.id]);

  // TEMP DEBUG - remove once the Node Lambda test payloads are sorted.
  useEffect(() => {
    if (!entry || !asset) return;
    console.log("TEST PAYLOAD DATA:", {
      asset_id: assetId,
      brand_id: entry.brand.id,
      identity: entry.identity,
      references: entry.references,
      brand: entry.brand,
      asset,
    });
  }, [entry, asset, assetId]);

  /**
   * The single choke-point every doc mutation goes through, so history
   * snapshots stay consistent. No persistence here — that's still only
   * the manual Save button, unchanged.
   */
  const update = useCallback((mutate) => {
    const prev = docRef.current;
    const next = mutate(prev);
    if (next === prev) return;
    // Snapshot for undo, coalescing rapid bursts (e.g. typing) into one step.
    const now = Date.now();
    if (now - lastSnapRef.current > 500) {
      historyRef.current.past.push({ doc: prev, label: describeChange(prev, next), at: now });
      if (historyRef.current.past.length > 40) historyRef.current.past.shift();
      historyRef.current.future = [];
      lastSnapRef.current = now;
      setHistTick((t) => t + 1);
    }
    docRef.current = next;
    setDoc(next);
  }, []);

  /** Restore a doc snapshot (undo/redo/jumpBack) without adding a new history entry. */
  const applyDoc = useCallback((next) => {
    docRef.current = next;
    setDoc(next);
  }, []);

  const undo = useCallback(() => {
    const h = historyRef.current;
    if (h.past.length === 0) return;
    h.future.push(docRef.current);
    const prev = h.past.pop();
    lastSnapRef.current = 0; // next edit starts a fresh snapshot
    setHistTick((t) => t + 1);
    applyDoc(prev.doc);
  }, [applyDoc]);

  const redo = useCallback(() => {
    const h = historyRef.current;
    if (h.future.length === 0) return;
    h.past.push({ doc: docRef.current, label: "Redo step", at: Date.now() });
    const next = h.future.pop();
    lastSnapRef.current = 0;
    setHistTick((t) => t + 1);
    applyDoc(next);
  }, [applyDoc]);

  // Jump back N steps in one go (from the history panel).
  const jumpBack = useCallback(
    (steps) => {
      const h = historyRef.current;
      let cur = docRef.current;
      let target = null;
      for (let i = 0; i < steps && h.past.length > 0; i++) {
        h.future.push(cur);
        const entry = h.past.pop();
        cur = entry.doc;
        target = cur;
      }
      if (target) {
        lastSnapRef.current = 0;
        setHistTick((t) => t + 1);
        applyDoc(target);
      }
    },
    [applyDoc]
  );


  /** Which advanced-layer field a given slot field mirrors, and that
   * layer's role - used by setSlot below to keep the two in sync. Only
   * headline/subline (+ their color ranges) have this correspondence;
   * every other slot field (logo, cta, colors, etc.) is untouched. */
  const TEXT_SYNC_FIELDS = {
    headline: { layerRole: "headline", layerField: "text" },
    headlineColorRanges: { layerRole: "headline", layerField: "colorRanges" },
    headlineWords: { layerRole: "headline", layerField: "words" },
    subline: { layerRole: "subline", layerField: "text" },
    sublineColorRanges: { layerRole: "subline", layerField: "colorRanges" },
    sublineWords: { layerRole: "subline", layerField: "words" },
    cta: { layerRole: "cta", layerField: "text" },
    ctaColorRanges: { layerRole: "cta", layerField: "colorRanges" },
    ctaWords: { layerRole: "cta", layerField: "words" },
  };

  /** Write a slot value into the master or the selected size's override. */
  const setSlot = useCallback(
    (field, value) => {
      update((current) => {
        if (selectedSize !== null) {
          // A per-size override has no corresponding concept on a layer
          // (layers are always global) - only the master value syncs.
          const override = { ...(current.overrides[selectedSize] ?? {}), [field]: value };
          return { ...current, overrides: { ...current.overrides, [selectedSize]: override } };
        }
        const next = { ...current, slots: { ...current.slots, [field]: value } };
        const sync = TEXT_SYNC_FIELDS[field];
        if (sync && next.layers) {
          next.layers = next.layers.map((l) =>
            l.role === sync.layerRole ? { ...l, [sync.layerField]: value } : l
          );
        }
        return next;
      });
    },
    [selectedSize, update]
  );

  const toggleBaked = useCallback(
    (field) => {
      update((current) => {
        const source = selectedSize
          ? (current.overrides[selectedSize]?.bakedText ?? current.slots.bakedText)
          : current.slots.bakedText;
        const next = new Set(source ?? []);
        if (next.has(field)) next.delete(field);
        else next.add(field);

        if (selectedSize === null) {
          return { ...current, slots: { ...current.slots, bakedText: [...next] } };
        }
        const override = { ...(current.overrides[selectedSize] ?? {}), bakedText: [...next] };
        return { ...current, overrides: { ...current.overrides, [selectedSize]: override } };
      });
    },
    [selectedSize, update]
  );

  const template = doc ? WIZARD_TEMPLATES.find((t) => t.id === doc.templateId) : null;
  const layouts = useMemo(
    () => (doc ? layoutsForTemplate(doc.templateId, doc.sizes) : {}),
    [doc]
  );

  const enterAdvancedFromTemplate = useCallback(() => {
    update((current) => ({
      ...current,
      advanced: true,
      layers: layersFromTemplate(current, layouts),
    }));
    setSelectedSize(null);
  }, [layouts, update]);

  // Resume a saved layout without regenerating it - the safe way back in
  // after exitAdvanced, as opposed to enterAdvancedFromTemplate which
  // always overwrites current.layers from scratch.
  const resumeAdvanced = useCallback(() => {
    update((current) => ({ ...current, advanced: true, layers: current.layers ?? [] }));
    setSelectedSize(null);
  }, [update]);

  const enterAdvancedBlank = useCallback(() => {
    update((current) => ({ ...current, advanced: true, layers: [] }));
    setSelectedSize(null);
  }, [update]);

  // Non-destructive: layers are kept so the layout can be resumed later.
  const exitAdvanced = useCallback(() => {
    update((current) => ({ ...current, advanced: false }));
    setSelectedLayerId(null);
  }, [update]);

  // --- Simple/advanced mode bypass -------------------------------------
  // Product decision: the simple-mode template editor adds little value:
  // bypass it entirely and go straight to advanced, without the person
  // ever seeing the "Customize layout" entry card. Underlying simple-mode
  // code (this function, the entry card UI, exitAdvanced/isAdvanced
  // throughout the codebase) is left fully intact, just unreachable via
  // UI, so this is reversible.
  //
  // To reintroduce simple mode later: delete this effect, and restore
  // the "Customize layout" entry card + "Exit to template" button
  // (search this file for "mode bypass" to find both).
  //
  // Deliberately bypasses the history-tracking `update()` wrapper (direct
  // setDoc/docRef assignment instead) so this system-initiated, invisible
  // conversion never becomes an undo-able history entry - undoing back to
  // a simple-mode state with no UI able to show or exit it would be a
  // confusing dead end, not a real "undo".
  useLayoutEffect(() => {
    if (!doc || isAdvanced(doc)) return;
    const hasExistingLayers = (doc.layers ?? []).length > 0;
    const next = {
      ...doc,
      advanced: true,
      layers: hasExistingLayers ? doc.layers : layersFromTemplate(doc, layouts),
    };
    docRef.current = next;
    setDoc(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc]);

  const addLayer = useCallback(
    (type, role) => {
      const id = crypto.randomUUID();
      update((current) => {
        const layers = current.layers ?? [];
        const maxZ = layers.reduce((m, l) => Math.max(m, l.z), -1);
        const boxes = {};
        for (const sizeId of current.sizes) {
          const size = displaySize(sizeId);
          if (!size) continue;
          boxes[sizeId] = {
            x: Math.round(size.w * 0.2),
            y: Math.round(size.h * 0.4),
            w: Math.round(size.w * 0.6),
            h: Math.round(size.h * 0.2),
          };
        }
        const layer =
          type === "text"
            ? {
                id,
                type: "text",
                role,
                z: maxZ + 1,
                text: role === "cta" ? "Shop now" : "New text",
                fontSize: 16,
                fontWeight: role === "cta" ? 600 : 400,
                align: "center",
                color: role === "cta" ? "#ffffff" : current.slots.textColor,
                bg: role === "cta" ? current.slots.ctaColor : null,
                boxes,
              }
            : { id, type: "image", role: "custom", z: maxZ + 1, fit: "cover", boxes };
        return { ...current, layers: [...layers, layer] };
      });
      setSelectedLayerId(id);
    },
    [update]
  );

  const deleteLayer = useCallback(
    (layerId) => {
      update((current) => ({
        ...current,
        layers: (current.layers ?? []).filter((l) => l.id !== layerId),
      }));
      setSelectedLayerId((cur) => (cur === layerId ? null : cur));
    },
    [update]
  );

  /** Persists a full reorder (top-first = front-most) for one size, from a drag-drop. */
  const setLayerOrder = useCallback(
    (idsTopFirst, sizeId) => {
      update((current) => {
        const n = idsTopFirst.length;
        const zById = new Map(idsTopFirst.map((id, i) => [id, n - i]));
        return {
          ...current,
          layers: (current.layers ?? []).map((l) =>
            zById.has(l.id) ? { ...l, zBySize: { ...(l.zBySize ?? {}), [sizeId]: zById.get(l.id) } } : l
          ),
        };
      });
    },
    [update]
  );

  const updateLayer = useCallback(
    (layerId, mutate) => {
      update((current) => {
        let updated = null;
        const layers = (current.layers ?? []).map((l) => {
          if (l.id !== layerId) return l;
          updated = typeof mutate === "function" ? mutate(l) : { ...l, ...mutate };
          return updated;
        });
        // Layers have no per-size text concept at all - a headline/
        // subline/cta layer's own text/colorRanges IS the master value,
        // unconditionally, so it always writes straight back to slots (a
        // harmless re-write of the same value on updates unrelated to
        // text, e.g. font size).
        let slots = current.slots;
        if (updated && (updated.role === "headline" || updated.role === "subline" || updated.role === "cta")) {
          slots = {
            ...slots,
            [updated.role]: updated.text,
            [`${updated.role}ColorRanges`]: updated.colorRanges,
            [`${updated.role}Words`]: updated.words,
          };
        }
        return { ...current, layers, slots };
      });
    },
    [update]
  );

  const setLayerBox = useCallback(
    (layerId, sizeId, partial) => {
      const isResize = "w" in partial || "h" in partial;
      update((current) => ({
        ...current,
        layers: (current.layers ?? []).map((l) => {
          if (l.id !== layerId) return l;
          const nextFontSizeBySize =
            isResize && l.fontSizeBySize?.[sizeId] !== undefined
              ? Object.fromEntries(Object.entries(l.fontSizeBySize).filter(([k]) => k !== sizeId))
              : l.fontSizeBySize;
          return {
            ...l,
            ...(nextFontSizeBySize !== l.fontSizeBySize ? { fontSizeBySize: nextFontSizeBySize } : {}),
            boxes: {
              ...l.boxes,
              [sizeId]: { ...(l.boxes[sizeId] ?? { x: 0, y: 0, w: 80, h: 24 }), ...partial },
            },
          };
        }),
      }));
    },
    [update]
  );

  const toggleLock = useCallback(
    (sizeId) => {
      update((current) => {
        // Unlock: bake the frozen look back into this banner's own state.
        if (isSizeLocked(current, sizeId)) return bakeLockedSnapshot(current, sizeId);
        // Lock: freeze a self-contained snapshot.
        const snaps = { ...(current.lockedSnapshots ?? {}), [sizeId]: buildLockedSnapshot(current, sizeId) };
        return { ...current, lockedSnapshots: snaps };
      });
    },
    [update]
  );

  /** Write the background into the shared master or the selected size's own override. */
  const setAdvancedBackground = useCallback(
    (source) => {
      update((current) => {
        if (selectedSize === null) {
          return { ...current, advancedBackground: source ?? undefined };
        }
        const nextBySize = { ...(current.backgroundBySize ?? {}) };
        if (source) nextBySize[selectedSize] = source;
        else delete nextBySize[selectedSize];
        return { ...current, backgroundBySize: nextBySize };
      });
    },
    [selectedSize, update]
  );

  // Deletes a size's override entirely (not just resets fields to blank) -
  // it goes back to fully inheriting from master.
  function resetOverride(sizeId) {
    update((current) => {
      const nextOverrides = { ...current.overrides };
      delete nextOverrides[sizeId];
      return { ...current, overrides: nextOverrides };
    });
  }

  function setDeliveryProfile(value) {
    update((current) => ({
      ...current,
      deliveryProfile: value === "self_hosted" ? "self_hosted" : undefined,
    }));
  }

  function setAnimationPreset(preset) {
    update((current) => ({ ...current, animationPreset: preset }));
  }

  function setComplianceLine(value) {
    update((current) => ({ ...current, slots: { ...current.slots, complianceLine: value } }));
  }

  // Small formats only: hide the subline or image on one size specifically
  // (e.g. a 320x50 has no room for both). runtime.js already checks
  // hidden.includes(...) when deciding what to render - this just writes
  // to the same override.hidden array resolveSlots already reads.
  function toggleHidden(sizeId, element) {
    update((current) => {
      const override = { ...(current.overrides[sizeId] ?? {}) };
      const hidden = new Set(override.hidden ?? []);
      if (hidden.has(element)) hidden.delete(element);
      else hidden.add(element);
      override.hidden = [...hidden];
      return { ...current, overrides: { ...current.overrides, [sizeId]: override } };
    });
  }

  function handleReplay() {
    setReplayKey((k) => k + 1);
  }

  function handleScrub(active) {
    previewPausedRef.current = active;
  }

  const copyLayer = useCallback(() => {
    const layer = (docRef.current?.layers ?? []).find((l) => l.id === selectedLayerId);
    if (layer) clipboardRef.current = layer;
  }, [selectedLayerId]);

  const pasteLayer = useCallback(() => {
    const src = clipboardRef.current;
    if (!src || !isAdvanced(docRef.current)) return;
    const id = crypto.randomUUID();
    update((current) => {
      const layers = current.layers ?? [];
      const maxZ = layers.reduce((m, l) => Math.max(m, l.z), -1);
      // deep clone + nudge each box so the copy is visible on top
      const boxes = {};
      for (const [sizeId, b] of Object.entries(src.boxes)) {
        const size = displaySize(sizeId);
        const x = size ? Math.min(b.x + 12, Math.max(0, size.w - b.w)) : b.x + 12;
        const y = size ? Math.min(b.y + 12, Math.max(0, size.h - b.h)) : b.y + 12;
        boxes[sizeId] = { ...b, x, y };
      }
      const clone = {
        ...structuredClone(src),
        id,
        name: `${layerName(src)} copy`,
        z: maxZ + 1,
        boxes,
      };
      return { ...current, layers: [...layers, clone] };
    });
    setSelectedLayerId(id);
  }, [update]);

  // Clicking a card (or its label) toggles selection — click the same one
  // again to deselect back to "All sizes", same as the original.
  function toggleSelectSize(sizeId) {
    const next = selectedSize === sizeId ? null : sizeId;
    setSelectedSize(next);
    setActiveSizeId(sizeId);
  }

  // "Edit this banner" behaves differently per mode: in advanced mode it
  // enters the dedicated focused/zoomed view; in slots mode there's no
  // focus view, so it's the same toggle as clicking the card.
  function editBanner(sizeId) {
    if (isAdvanced(doc)) {
      setFocusSize(sizeId);
      setSelectedSize(sizeId);
      setActiveSizeId(sizeId);
      setZoom(1);
    } else {
      toggleSelectSize(sizeId);
    }
  }

  const [uploadError, setUploadError] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadApiResponse, setUploadApiResponse] = useState(null);

  // Uploads a new image and persists it as a real, reusable reference (not
  // a transient local file) — unlike the wizard's BYO upload, an image
  // picked here is meant to stick around for future edits too.
  async function handleUploadImage(kind, file) {
    setUploadError(null);

    if (!file.type.startsWith("image/")) {
      setUploadError("Only image files are supported (PNG, JPG, WebP, SVG, GIF).");
      return null;
    }
    if (file.size > LIMITS.MAX_UPLOAD_MB * 1024 * 1024) {
      setUploadError(
        `That file is ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit is ${LIMITS.MAX_UPLOAD_MB} MB.`
      );
      return null;
    }

    try {
      const { referenceId, key, url } = await uploadReference(
        config,
        { brandId: entry.brand.id, kind, file, currentReferenceCount: (entry.references ?? []).length },
        setIsUploadingImage,
        setUploadApiResponse
      );
      const newReference = {
        id: referenceId,
        kind,
        label: null,
        url,
        key,
        createdAt: new Date().toISOString(),
      };
      await onSaveReferences(entry.brand.id, [...(entry.references ?? []), newReference]);
      return { kind: "reference", id: referenceId };
    } catch (err) {
      setUploadError("Upload failed. Please try again.");
      return null;
    }
  }

  const hasUnsavedChanges = doc && asset && JSON.stringify(doc) !== JSON.stringify(asset.content);

  async function handleSave() {
    setSaveError(null);
    try {
      const updated = await onUpdateAsset(entry.brand.id, assetId, doc);
      setAsset(updated);
    } catch (err) {
      setSaveError("Couldn't save this asset. Please try again.");
    }
  }

  /**
   * Polls getAsset until exportJob clears (either succeeds or fails) or a
   * client-side timeout is hit. A stuck job self-heals server-side too
   * (EXPORT_JOB_STALE_SECONDS = 180s) - timeoutMs here is kept a little
   * under that so a client timeout and a server-side stale job line up,
   * rather than leaving a window where re-triggering just hits the
   * "already running" guard. This is purely about not leaving the user
   * staring at a spinner forever if a heavy export takes a while - the
   * job itself is still running server-side either way.
   */
  async function handleApprove() {
    if (hasUnsavedChanges) {
      setActionError("You have unsaved changes — save first so approval reflects your latest edits.");
      return;
    }
    setGatePending(true);
    setActionError(null);
    try {
      const updated = await approveAsset(config, { brandId: entry.brand.id, assetId }, () => {}, () => {});
      setAsset(updated);
    } catch (err) {
      setActionError(err.message || "Couldn't approve this asset. Please try again.");
    } finally {
      setGatePending(false);
    }
  }

  async function handleRevertToDraft() {
    if (asset.status === "exported") {
      const ok = window.confirm(
        "This banner has already been exported — real files may already be downloaded and in use. " +
          "Reverting to draft won't affect those existing downloads, but you'll need to approve and " +
          "export again to get updated files. Continue?"
      );
      if (!ok) return;
    }
    setActionError(null);
    try {
      const updated = await revertToDraft(config, { brandId: entry.brand.id, assetId }, () => {}, () => {});
      setAsset(updated);
    } catch (err) {
      setActionError(err.message || "Couldn't revert this asset to draft.");
    }
  }

  /** Char budget for a text slot: the tightest box among the affected sizes. */
  const budgetFor = useCallback(
    (slotName) => {
      if (!doc) return 40;
      const sizes = selectedSize ? [selectedSize] : doc.sizes;
      let budget = Infinity;
      for (const sizeId of sizes) {
        const box = layouts[sizeId]?.[slotName];
        if (box) budget = Math.min(budget, slotCharBudget(box));
      }
      return budget === Infinity ? 40 : budget;
    },
    [doc, selectedSize, layouts]
  );

  // Ctrl/Cmd+Z undo, Ctrl/Cmd+Shift+Z or Ctrl+Y redo, Ctrl/Cmd+C/V copy/paste
  // an element (advanced mode only). Skipped while focus is in a text
  // field so normal browser undo/copy/paste there keeps working.
  useEffect(() => {
    const onKey = (e) => {
      const editableNow = asset ? asset.status === "draft" && canEdit(config.userRole) : false;
      if (!editableNow || !(e.metaKey || e.ctrlKey)) return;
      const el = document.activeElement;
      const inField =
        !!el &&
        (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);
      if (inField) return;
      const advancedNow = doc ? isAdvanced(doc) : false;
      const key = e.key.toLowerCase();
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((key === "z" && e.shiftKey) || key === "y") {
        e.preventDefault();
        redo();
      } else if (key === "c" && advancedNow && selectedLayerId && !window.getSelection()?.toString()) {
        e.preventDefault();
        copyLayer();
      } else if (key === "v" && advancedNow && clipboardRef.current) {
        e.preventDefault();
        pasteLayer();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [asset, doc, selectedLayerId, config, undo, redo, copyLayer, pasteLayer]);

  // Preview auto-loops: restart every preview's animations once the timeline
  // ends, so the banner keeps playing while authoring (export is unaffected
  // — it still honors the loop count / delivery profile). Suspended while
  // scrubbing. Needs sandbox="allow-same-origin" on the preview iframes
  // (below) to reach contentDocument; safe here since our srcDoc output is
  // static HTML/CSS with no scripts of its own.
  const loopMs = useMemo(() => {
    if (!doc) return 3000;
    const secs = hasLayerContent(doc) ? advancedTimelineDuration(doc) : presetDuration(doc.animationPreset);
    return Math.max(1500, Math.round((secs || 3) * 1000) + 700);
  }, [doc]);
  useEffect(() => {
    const id = setInterval(() => {
      if (previewPausedRef.current) return;
      document.querySelectorAll('iframe[title^="Preview "]').forEach((ifr) => {
        const d = ifr.contentDocument;
        if (!d || typeof d.getAnimations !== "function") return;
        d.getAnimations().forEach((a) => {
          try {
            a.currentTime = 0;
            a.play();
          } catch {
            // animation may have been GC'd mid-loop - harmless, skip it
          }
        });
      });
    }, loopMs);
    return () => clearInterval(id);
  }, [loopMs]);

  if (!entry) {
    return (
      <div className="compose-page__status">
        <p>Missing brand context for this asset.</p>
        <Link to="/brands" className="compose-page__back-link">
          Back to Brands
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return <p className="compose-page__status">Loading asset…</p>;
  }

  if (error) {
    return <p className="compose-page__status compose-page__status--error">{error}</p>;
  }

  if (!asset || !doc) {
    return (
      <div className="compose-page__status">
        <p>This asset has no content yet.</p>
        <Link to={`/brands/${entry.brand.slug}`} className="compose-page__back-link">
          Back to {entry.brand.name}
        </Link>
      </div>
    );
  }

  const regulated = isRegulated(entry.identity);
  // Compares this asset's own, already-baked compliance line against the
  // brand's CURRENT compliance text - createInitialBannerDoc only ever
  // computes this once, at asset-creation time, so a later edit to the
  // brand's identity never propagates to an already-created asset's own
  // doc automatically. null vs null (neither ever had one) isn't a
  // meaningful difference to flag.
  const complianceStale =
    (doc.slots.complianceLine ?? null) !== (canonicalCompliance(entry.identity) ?? null);
  const advanced = isAdvanced(doc);
  const hasSavedLayout = Array.isArray(doc.layers);
  const editable = asset.status === "draft" && canEdit(config.userRole);
  const canUndo = historyRef.current.past.length > 0;
  const canRedo = historyRef.current.future.length > 0;
  // Newest-first list of states you can return to (last 20).
  const historyList = [...historyRef.current.past].reverse().slice(0, 20);
  void histTick; // recompute canUndo/canRedo/historyList on each history change
  // The values shown in the editing fields: master, or the selected size's cascade.
  const editing = selectedSize ? resolveSlots(doc, selectedSize) : { slots: doc.slots, hidden: [] };
  const editingSlots = editing.slots;
  const bakedSet = new Set(editingSlots.bakedText ?? []);

  const ctx = buildRenderContext(doc, entry.references);
  const previews = doc.sizes.map((sizeId) => {
    const info = displaySize(sizeId);
    const layout = layouts[sizeId];
    const scale = info ? Math.min(1, 340 / info.w) : 1;
    return {
      sizeId,
      info,
      scale,
      srcDoc: info && layout ? renderBannerSrcdoc(doc, sizeId, layout, ctx) : null,
    };
  });

  return (
    <div className="compose-page">
      <div className="compose-page__header">
        <div>
          <h1 className="compose-page__title">{asset.publicAssetCode ?? asset.id}</h1>
          <div className="compose-page__meta-row">
            <span className="compose-page__status-badge">{STATUS_LABEL[asset.status] ?? asset.status}</span>
            <span className="compose-page__subtitle">
              HTML5 display ·{" "}
              <Link to={`/brands/${entry.brand.slug}`} className="compose-page__inline-link">
                {entry.brand.name}
              </Link>{" "}
              · {template?.name ?? doc.templateId}
              {hasUnsavedChanges ? <span className="compose-page__unsaved-dot" /> : null}
            </span>
          </div>
        </div>
        <div className="compose-page__header-actions">
          <button
            type="button"
            onClick={handleSave}
            disabled={!hasUnsavedChanges || isSavingAsset || !editable}
            title={
              !editable && hasUnsavedChanges
                ? "Editors and admins only"
                : !hasUnsavedChanges
                  ? "No changes to save"
                  : undefined
            }
            className="compose-page__save-btn"
          >
            {isSavingAsset ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {saveError ? <p className="compose-page__save-error">{saveError}</p> : null}

      <div className="compose-page__gate">
        {asset.status === "draft" ? (
          <button
            type="button"
            disabled={gatePending || hasUnsavedChanges || !canEdit(config.userRole)}
            title={
              !canEdit(config.userRole)
                ? "Editors and admins only"
                : hasUnsavedChanges
                  ? "Save your changes first"
                  : undefined
            }
            onClick={handleApprove}
            className="compose-page__approve-btn"
          >
            {gatePending ? "Approving…" : "Approve banner set"}
          </button>
        ) : (
          <>
            <button
              type="button"
              disabled={hasUnsavedChanges}
              title={hasUnsavedChanges ? "Save your changes first" : undefined}
              onClick={() => setExportDialogOpen(true)}
              className="compose-page__export-btn"
            >
              Build HTML5 zips
            </button>
            {asset.status === "approved" || asset.status === "exported" ? (
              <button
                type="button"
                disabled={!canEdit(config.userRole)}
                title={!canEdit(config.userRole) ? "Editors and admins only" : undefined}
                onClick={handleRevertToDraft}
                className="compose-page__revert-btn"
              >
                Back to draft
              </button>
            ) : null}
          </>
        )}
        {/* Site preview is for display placements only - hidden if every size is paid-social. */}
        {doc.sizes.some((s) => displaySize(s)?.channel !== "social") ? (
          <button type="button" onClick={() => setSiteOpen(true)} className="compose-page__preview-btn">
            🌐 Preview on a site
          </button>
        ) : null}
        {hasUnsavedChanges ? (
          <span className="compose-page__gate-hint">Save your changes before approving or exporting.</span>
        ) : asset.status === "draft" && !canEdit(config.userRole) ? (
          <span className="compose-page__gate-hint">Viewers can export an already-approved asset, but can't approve one themselves.</span>
        ) : null}
        {asset.exports?.length > 0 ? (
          <a
            href={asset.exports[asset.exports.length - 1].downloadUrl}
            target="_blank"
            rel="noreferrer"
            className="compose-page__download-link"
          >
            Download latest export ({new Date(asset.exports[asset.exports.length - 1].createdAt).toLocaleString()})
          </a>
        ) : null}
      </div>

      {actionError ? <p className="compose-page__gate-error">{actionError}</p> : null}

      {editable ? (
        <div className="compose-toolbar">
          <button
            type="button"
            onClick={undo}
            disabled={!canUndo}
            title="Undo (Ctrl+Z)"
            className="compose-toolbar__btn"
          >
            ↶ Undo
          </button>
          <button
            type="button"
            onClick={redo}
            disabled={!canRedo}
            title="Redo (Ctrl+Shift+Z)"
            className="compose-toolbar__btn"
          >
            ↷ Redo
          </button>
          <div className="compose-toolbar__history">
            <button
              type="button"
              onClick={() => setHistoryOpen((o) => !o)}
              disabled={!canUndo}
              aria-expanded={historyOpen}
              title="Recent changes"
              className="compose-toolbar__btn"
            >
              History ▾
            </button>
            {historyOpen && canUndo ? (
              <>
                <div className="compose-toolbar__history-scrim" onClick={() => setHistoryOpen(false)} aria-hidden />
                <div className="compose-toolbar__history-menu">
                  <p className="compose-toolbar__history-label">Go back to…</p>
                  {historyList.map((entry_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => {
                        jumpBack(i + 1);
                        setHistoryOpen(false);
                      }}
                      className="compose-toolbar__history-item"
                    >
                      <span className="compose-toolbar__history-step">−{i + 1}</span>
                      <span className="compose-toolbar__history-item-label">{entry_.label}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : null}
          </div>
          {advanced ? (
            <>
              <span className="compose-toolbar__divider" />
              <button
                type="button"
                onClick={copyLayer}
                disabled={!selectedLayerId}
                title="Copy element (Ctrl+C)"
                className="compose-toolbar__btn"
              >
                Copy
              </button>
              <button
                type="button"
                onClick={pasteLayer}
                title="Paste element (Ctrl+V)"
                className="compose-toolbar__btn"
              >
                Paste
              </button>
            </>
          ) : null}
        </div>
      ) : null}

      <div className="compose-page__body">
        <div className="compose-page__preview-column">
          {advanced && focusSize ? (
            <FocusedView
              doc={doc}
              sizeId={focusSize}
              layouts={layouts}
              ctx={ctx}
              references={entry.references}
              zoom={zoom}
              setZoom={setZoom}
              selectedLayerId={selectedLayerId}
              onSelectLayer={setSelectedLayerId}
              onSetLayerBox={setLayerBox}
              onBack={() => setFocusSize(null)}
              editable={editable}
              onToggleLock={toggleLock}
              replayKey={replayKey}
              onSetDeliveryProfile={setDeliveryProfile}
              onReplay={handleReplay}
              onScrub={handleScrub}
              onUpdateLayer={updateLayer}
              palette={entry.identity?.palette ?? []}
              onUploadImage={handleUploadImage}
              isUploadingImage={isUploadingImage}
              uploadError={uploadError}
            />
          ) : (
            <>
              <div className="compose-page__positioning">
                <span className="compose-page__positioning-label">Positioning for</span>
                <div className="compose-page__positioning-pills">
                  <button
                    type="button"
                    onClick={() => setSelectedSize(null)}
                    className={`compose-page__pill${
                      selectedSize === null ? " compose-page__pill--active" : ""
                    }`}
                  >
                    All sizes
                  </button>
                  {doc.sizes.map((sizeId) => (
                    <button
                      key={sizeId}
                      type="button"
                      onClick={() => toggleSelectSize(sizeId)}
                      className={`compose-page__pill${
                        sizeId === selectedSize ? " compose-page__pill--active" : ""
                      }`}
                    >
                      {sizeId}
                    </button>
                  ))}
                </div>
                <p className="compose-page__positioning-hint">
                  {selectedSize
                    ? `Each size has its own positions. Editing ${selectedSize}.`
                    : "Editing the shared version every size falls back to."}
                </p>
              </div>

              <div className="compose-page__grid">
                {previews.map(({ sizeId, info, scale, srcDoc }) => {
                  const isSelected = sizeId === selectedSize;
                  return (
                    <figure key={sizeId} className="compose-page__card">
                      <figcaption
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleSelectSize(sizeId)}
                        onKeyDown={(e) => e.key === "Enter" && toggleSelectSize(sizeId)}
                        className={`compose-page__card-label${
                          isSelected ? " compose-page__card-label--active" : ""
                        }`}
                      >
                        {sizeId}
                      </figcaption>
                      <div
                        role="button"
                        tabIndex={0}
                        aria-label={`Edit ${sizeId}`}
                        onClick={() => toggleSelectSize(sizeId)}
                        onKeyDown={(e) => e.key === "Enter" && toggleSelectSize(sizeId)}
                        className={`compose-page__card-preview${
                          isSelected ? " compose-page__card-preview--selected" : ""
                        }`}
                        style={info ? { width: info.w * scale, height: info.h * scale } : undefined}
                      >
                        {srcDoc && info ? (
                          <iframe
                            key={previewKeyFor(sizeId, doc, entry.references, replayKey)}
                            title={`Preview ${sizeId}`}
                            srcDoc={srcDoc}
                            width={info.w}
                            height={info.h}
                            sandbox="allow-same-origin"
                            style={{
                              width: info.w,
                              height: info.h,
                              transform: `scale(${scale})`,
                              transformOrigin: "top left",
                              pointerEvents: "none",
                            }}
                            className="compose-page__iframe"
                          />
                        ) : null}
                        {advanced && info && !isSizeLocked(doc, sizeId) ? (
                          <ComposeOverlay
                            sizeId={sizeId}
                            scale={scale}
                            layers={doc.layers ?? []}
                            selectedLayerId={selectedLayerId}
                            editable={editable}
                            onSelectLayer={setSelectedLayerId}
                            onSetLayerBox={setLayerBox}
                          />
                        ) : null}
                      </div>
                      <button
                        type="button"
                        onClick={() => editBanner(sizeId)}
                        style={info ? { width: info.w * scale } : undefined}
                        className={`compose-page__card-edit-btn${
                          isSelected ? " compose-page__card-edit-btn--active" : ""
                        }`}
                      >
                        {advanced
                          ? "Edit this banner →"
                          : isSelected
                            ? "✓ Editing this banner"
                            : "Edit this banner"}
                      </button>
                    </figure>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="compose-page__summary">
          {advanced ? (
            <>
              {/* Simple/advanced mode bypass: hidden so the person can't
                  navigate back into a mode with no supported entry point
                  of its own anymore (see the auto-conversion effect
                  earlier in this file, search "mode bypass"). */}
              {SHOW_EXIT_TO_SIMPLE_MODE ? (
                <button type="button" onClick={exitAdvanced} className="layers-panel__exit-btn">
                  ← Back to simple editing
                </button>
              ) : null}
              <LayersPanel
                doc={doc}
                activeSizeId={activeSizeId}
                selectedLayerId={selectedLayerId}
                references={entry.references}
                regulated={regulated}
                editable={editable}
                palette={entry.identity?.palette ?? []}
                selectedSize={selectedSize}
                ctx={ctx}
                onSelectLayer={setSelectedLayerId}
                onAddLayer={addLayer}
                onDeleteLayer={deleteLayer}
                onSetLayerOrder={setLayerOrder}
                onUpdateLayer={updateLayer}
                onSetLayerBox={setLayerBox}
                onSetBackground={setAdvancedBackground}
                onSetComplianceLine={setComplianceLine}
                complianceStale={complianceStale}
                onUploadImage={handleUploadImage}
                isUploadingImage={isUploadingImage}
                uploadError={uploadError}
              />
            </>
          ) : (
            <>
              {selectedSize ? (
                <div className="compose-page__notice compose-page__notice--active">
                  <span className="compose-page__notice-title">Editing {selectedSize}</span>
                  <span className="compose-page__notice-actions">
                    <button
                      type="button"
                      onClick={() => resetOverride(selectedSize)}
                      className="compose-page__notice-link"
                    >
                      Reset to master
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedSize(null)}
                      className="compose-page__notice-link compose-page__notice-link--muted"
                    >
                      Back to master
                    </button>
                  </span>
                </div>
              ) : (
                <div className="compose-page__notice">
                  Editing the <strong>master</strong> — changes cascade to every size. Click a
                  preview to override one size.
                </div>
              )}

              <div className="layers-panel__entry">
                {!editable ? (
                  <p className="layers-panel__entry-role-notice">
                    {asset.status !== "draft" ? "Locked" : "Editors and admins only"} — layout
                    changes aren't available here.
                  </p>
                ) : null}
                {hasSavedLayout ? (
                  <>
                    <p className="layers-panel__entry-title">Custom layout saved</p>
                    <p className="layers-panel__entry-desc">
                      Your free-positioned layout is kept. Resume editing, or start over.
                    </p>
                    <div className="layers-panel__entry-buttons">
                      <button
                        type="button"
                        disabled={!editable}
                        onClick={resumeAdvanced}
                        className="layers-panel__entry-primary"
                      >
                        Resume layout →
                      </button>
                      <button
                        type="button"
                        disabled={!editable}
                        onClick={enterAdvancedFromTemplate}
                        className="layers-panel__entry-secondary"
                      >
                        Reset from template
                      </button>
                      <button
                        type="button"
                        disabled={!editable}
                        onClick={enterAdvancedBlank}
                        className="layers-panel__entry-secondary"
                      >
                        Blank canvas
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="layers-panel__entry-title">Customize layout</p>
                    <p className="layers-panel__entry-desc">
                      Move each headline, subline, CTA, logo or image anywhere per size, swap any
                      text element for an uploaded image, animate each one, or start from an
                      empty canvas.
                    </p>
                    <div className="layers-panel__entry-buttons">
                      <button
                        type="button"
                        disabled={!editable}
                        onClick={enterAdvancedFromTemplate}
                        className="layers-panel__entry-primary"
                      >
                        From this template →
                      </button>
                      <button
                        type="button"
                        disabled={!editable}
                        onClick={enterAdvancedBlank}
                        className="layers-panel__entry-secondary"
                      >
                        Blank canvas
                      </button>
                    </div>
                  </>
                )}
              </div>
              <EditingPanel
                doc={doc}
                editingSlots={editingSlots}
                editingHidden={editing.hidden}
                selectedSize={selectedSize}
                identity={entry.identity}
                references={entry.references}
                regulated={regulated}
                editable={editable}
                budgetFor={budgetFor}
                setSlot={setSlot}
                toggleBaked={toggleBaked}
                bakedSet={bakedSet}
                onToggleHidden={toggleHidden}
                onSetDeliveryProfile={setDeliveryProfile}
                onSetAnimationPreset={setAnimationPreset}
                onReplay={handleReplay}
                onUploadImage={handleUploadImage}
                isUploadingImage={isUploadingImage}
                uploadError={uploadError}
              />
            </>
          )}
        </div>
      </div>

      {exportDialogOpen ? (
        <ExportDialog
          config={config}
          entry={entry}
          assetId={assetId}
          asset={asset}
          doc={doc}
          advanced={advanced}
          onClose={() => setExportDialogOpen(false)}
          onExported={(updatedAsset) => setAsset(updatedAsset)}
        />
      ) : null}

      {siteOpen ? (
        <SitePreviewDialog
          sizes={doc.sizes.filter((s) => displaySize(s)?.channel !== "social")}
          srcdocFor={(sizeId) => renderBannerSrcdoc(doc, sizeId, layouts[sizeId], ctx)}
          previewKeyFor={(sizeId) => previewKeyFor(sizeId, doc, entry.references, replayKey)}
          onClose={() => setSiteOpen(false)}
        />
      ) : null}
    </div>
  );
}

function FocusedView({
  doc,
  sizeId,
  layouts,
  ctx,
  references,
  zoom,
  setZoom,
  selectedLayerId,
  onSelectLayer,
  onSetLayerBox,
  onBack,
  editable,
  onToggleLock,
  replayKey,
  onSetDeliveryProfile,
  onReplay,
  onScrub,
  onUpdateLayer,
  palette,
  onUploadImage,
  isUploadingImage,
  uploadError,
}) {
  const info = displaySize(sizeId);
  const layout = layouts[sizeId];

  // Measures the actual, rendered available space (not a hardcoded
  // guess) and fits zoom to it - runs after mount, when the container
  // genuinely has a size to measure, and again whenever it resizes or a
  // different size is focused. Placed before the early return below,
  // per the Rules of Hooks - a hook can't be called conditionally.
  const scrollRef = useRef(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!expanded) return;
    function handleKey(e) {
      if (e.key === "Escape") setExpanded(false);
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [expanded]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !info) return;
    const PADDING = 40; // breathing room so the canvas doesn't touch the edges
    const fit = () => {
      const availableW = el.clientWidth - PADDING;
      const availableH = el.clientHeight - PADDING;
      setZoom(computeFitZoom(info.w, info.h, availableW, availableH));
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
    // Re-fit when the focused size changes (a different banner's own
    // dimensions need a fresh calculation) - not when zoom itself
    // changes, which would fight the person's own manual +/- clicks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sizeId, info?.w, info?.h]);

  if (!info || !layout) {
    return (
      <button type="button" onClick={onBack} className="compose-page__focus-back">
        ← All banners
      </button>
    );
  }

  const locked = isSizeLocked(doc, sizeId);
  const srcDoc = renderBannerSrcdoc(doc, sizeId, layout, ctx);
  const selectedLayer = (doc.layers ?? []).find((l) => l.id === selectedLayerId) ?? null;
  const pickableImages = (references ?? []).filter((r) => r.kind === "product" || r.kind === "example");

  return (
    <div className={`compose-page__focus-view${expanded ? " compose-page__focus-view--expanded" : ""}`}>
      <div className="compose-page__focus-toolbar">
        <button type="button" onClick={onBack} className="compose-page__focus-back">
          ← All banners
        </button>
        <span className="compose-page__focus-size">{sizeId}</span>
        <div className="compose-page__focus-zoom">
          <button
            type="button"
            onClick={() => setZoom((z) => clampZoom(z - 0.25))}
            aria-label="Zoom out"
            className="compose-page__zoom-btn"
          >
            −
          </button>
          <span className="compose-page__zoom-value">{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            onClick={() => setZoom((z) => clampZoom(z + 0.25))}
            aria-label="Zoom in"
            className="compose-page__zoom-btn"
          >
            +
          </button>
          <button
            type="button"
            onClick={() => {
              const el = scrollRef.current;
              if (!el) return;
              setZoom(computeFitZoom(info.w, info.h, el.clientWidth - 40, el.clientHeight - 40));
            }}
            className="compose-page__zoom-fit"
          >
            Fit
          </button>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-pressed={expanded}
          className="compose-page__expand-btn"
        >
          {expanded ? "✕ Exit expanded view" : "⛶ Expand"}
        </button>
        <button
          type="button"
          disabled={!editable}
          onClick={() => onToggleLock(sizeId)}
          className={`compose-page__lock-btn${locked ? " compose-page__lock-btn--locked" : ""}`}
        >
          {locked ? "🔒 Locked" : "🔓 Lock"}
        </button>
      </div>

      {(() => {
        // Tall/vertical formats (skyscrapers, half-pages) squeeze the
        // canvas into a small vertical share when the Timeline panel
        // sits below it in the same column - both compete for the same
        // limited height, and the canvas's own width then shrinks
        // proportionally too (aspect ratio is fixed), leaving large,
        // wasted horizontal space next to a narrow banner. Confirmed
        // directly: the stacked layout produces roughly half the zoom
        // level a side-by-side one does for the same 160x600 case.
        // Placing the Timeline beside the canvas instead gives the
        // canvas the FULL available height, and puts that otherwise-
        // wasted horizontal space to actual use.
        const isTall = info.h / info.w >= 1.3;
        const timeline = (
          <ComposeTimelinePanel
            doc={doc}
            selectedLayerId={selectedLayerId}
            onSelectLayer={onSelectLayer}
            editable={editable}
            onSetDeliveryProfile={onSetDeliveryProfile}
            onReplay={onReplay}
            onScrub={onScrub}
            onUpdateLayer={onUpdateLayer}
          />
        );

        return (
          <div className={`compose-page__focus-body${isTall ? " compose-page__focus-body--tall" : ""}`}>
            {expanded && selectedLayer ? (
              <div className="compose-page__focus-editor-col">
                <LayerEditor
                  layer={selectedLayer}
                  sizeId={sizeId}
                  images={pickableImages}
                  editable={editable}
                  palette={palette}
                  ctx={ctx}
                  defaultBgColor={doc.slots.ctaColor}
                  allowInfinite={doc.deliveryProfile === "self_hosted"}
                  onUpdateLayer={onUpdateLayer}
                  onSetLayerBox={onSetLayerBox}
                  onUploadImage={onUploadImage}
                  isUploadingImage={isUploadingImage}
                  uploadError={uploadError}
                />
              </div>
            ) : null}

            <div className="compose-page__focus-main">
              <div className="compose-page__focus-scroll" ref={scrollRef}>
                <div
                  className="compose-page__focus-canvas"
                  style={{ width: info.w * zoom, height: info.h * zoom }}
                >
                  <iframe
                    key={previewKeyFor(sizeId, doc, references, replayKey)}
                    title={`Preview ${sizeId}`}
                    srcDoc={srcDoc}
                    width={info.w}
                    height={info.h}
                    sandbox="allow-same-origin"
                    style={{
                      width: info.w,
                      height: info.h,
                      transform: `scale(${zoom})`,
                      transformOrigin: "top left",
                      pointerEvents: "none",
                    }}
                    className="compose-page__iframe"
                  />
                  {!locked ? (
                    <ComposeOverlay
                      sizeId={sizeId}
                      scale={zoom}
                      layers={doc.layers ?? []}
                      selectedLayerId={selectedLayerId}
                      editable={editable}
                      onSelectLayer={onSelectLayer}
                      onSetLayerBox={onSetLayerBox}
                    />
                  ) : null}
                </div>
              </div>
              <p className="compose-page__focus-caption">
                Editing <span className="compose-page__focus-caption-size">{sizeId}</span> on its own —
                position and order here affect only this banner.
              </p>
              {!isTall ? timeline : null}
            </div>

            {isTall ? <div className="compose-page__focus-timeline-col">{timeline}</div> : null}
          </div>
        );
      })()}
    </div>
  );
}